import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
    const [logoPreview, setLogoPreview] = useState(null);

    const { data: stats, isLoading: statsLoading } = useQuery(
        'userStats',
        () => axios.get('/users/stats').then(res => res.data),
        { refetchInterval: 30000 }
    );

    const { data: recentJobs, isLoading: jobsLoading } = useQuery(
        'recentJobs',
        () => axios.get('/users/jobs?limit=5').then(res => res.data),
        { refetchInterval: 10000 }
    );

    const { data: heygenInfo, isLoading: heygenLoading } = useQuery(
        'heygenUserInfo',
        () => axios.get('/heygen/user-info').then(res => res.data),
        { refetchInterval: 60000 }
    );

    const quickActions = [
        {
            title: 'Generate Video',
            description: 'Create AI videos with avatars and voices.',
            icon: 'GV',
            link: '/video-generation',
            accent: 'action-card--blue'
        },
        {
            title: 'Streaming Avatar',
            description: 'Host real-time, interactive avatar sessions.',
            icon: 'SA',
            link: '/streaming-avatar',
            accent: 'action-card--teal'
        },
        {
            title: 'View Jobs',
            description: 'Track the progress of every render and stream.',
            icon: 'JB',
            link: '/jobs',
            accent: 'action-card--purple'
        },
        {
            title: 'API Docs',
            description: 'Explore HeyGen endpoints and implementation tips.',
            icon: 'API',
            link: '/api-docs',
            accent: 'action-card--magenta'
        }
    ];

    const metrics = [
        {
            label: 'Total Jobs',
            value: stats?.stats?.total ?? 0,
            loading: statsLoading,
            footnote: 'Across every workflow',
            accentClass: 'metric-accent--blue'
        },
        {
            label: 'In Progress',
            value: stats?.stats?.processing ?? 0,
            loading: statsLoading,
            footnote: 'Currently rendering or streaming',
            accentClass: 'metric-accent--teal'
        },
        {
            label: 'Completed Jobs',
            value: stats?.stats?.completed ?? 0,
            loading: statsLoading,
            footnote: 'Ready for download or playback',
            accentClass: 'metric-accent--green'
        },
        {
            label: 'HeyGen Credits',
            value: heygenInfo?.usage?.remaining_quota ?? 'N/A',
            loading: heygenLoading,
            footnote: 'Remaining balance',
            accentClass: 'metric-accent--magenta'
        }
    ];

    const jobTypeLabel = (type) => {
        if (!type) return 'Unknown job';
        const withSpaces = type.replace(/_/g, ' ');
        return withSpaces.replace(/\b\w/g, (letter) => letter.toUpperCase());
    };

    const jobBadge = (type) => {
        if (!type) return 'JB';
        const lettersOnly = type.replace(/[^a-z]/gi, '');
        const code = lettersOnly.slice(0, 2).toUpperCase();
        return code || 'JB';
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'badge-pending',
            processing: 'badge-processing',
            completed: 'badge-completed',
            failed: 'badge-failed'
        };
        return badges[status] || 'badge-pending';
    };

    const remainingCredits = heygenLoading
        ? 'Loading credits...'
        : `${heygenInfo?.usage?.remaining_quota ?? 'N/A'} credits`;

    const logoSrc = logoPreview || `${process.env.PUBLIC_URL || ''}/oygen-logo.png`;

    const handleLogoUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                setLogoPreview(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="dashboard-page">
            <div className="container dashboard-container">
                <section className="dashboard-hero glass-panel">
                    <div className="dashboard-hero__logo">
                        <img src={logoSrc} alt="OyGen logo" />
                        <label className="logo-upload">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                aria-label="Upload a custom OyGen logo"
                            />
                            Change logo
                        </label>
                    </div>
                    <div className="dashboard-hero__content">
                        <h1>Welcome to OyGen Control Center</h1>
                        <p>
                            Launch, monitor, and optimise your HeyGen powered experiences with a
                            dashboard styled around the OyGen brand.
                        </p>
                        <div className="dashboard-hero__insights">
                            <div className="hero-pill">
                                <span className="hero-pill__label">API Status</span>
                                <span className="hero-pill__value">Connected</span>
                            </div>
                            <div className="hero-pill">
                                <span className="hero-pill__label">Credit Balance</span>
                                <span className="hero-pill__value">{remainingCredits}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="metric-grid">
                    {metrics.map((metric) => (
                        <div key={metric.label} className="metric-card glass-panel">
                            <div className={`metric-card__accent ${metric.accentClass}`}></div>
                            <p className="metric-card__label">{metric.label}</p>
                            <p className="metric-card__value">
                                {metric.loading ? <LoadingSpinner size="small" /> : metric.value}
                            </p>
                            <p className="metric-card__footnote">{metric.footnote}</p>
                        </div>
                    ))}
                </section>

                <section className="quick-actions">
                    <div className="section-heading">
                        <div>
                            <h2>Accelerate your workflow</h2>
                            <p>Jump into the most common OyGen tasks in a single tap.</p>
                        </div>
                        <Link to="/api-docs" className="heading-link">
                            Developer resources
                        </Link>
                    </div>

                    <div className="quick-actions__grid">
                        {quickActions.map((action) => (
                            <Link
                                key={action.title}
                                to={action.link}
                                className={`quick-action-card ${action.accent}`}
                            >
                                <div className="quick-action-card__icon">{action.icon}</div>
                                <div>
                                    <h3>{action.title}</h3>
                                    <p>{action.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                <section className="jobs-card glass-panel">
                    <div className="section-heading">
                        <div>
                            <h2>Recent activity</h2>
                            <p>The latest five jobs across your OyGen workspace.</p>
                        </div>
                        <Link to="/jobs" className="heading-link">
                            View all jobs
                        </Link>
                    </div>

                    <div className="jobs-card__content">
                        {jobsLoading ? (
                            <div className="jobs-loading">
                                <LoadingSpinner />
                            </div>
                        ) : recentJobs?.jobs?.length === 0 ? (
                            <div className="jobs-empty">
                                <div className="jobs-empty__badge">Start</div>
                                <h3>No jobs yet</h3>
                                <p>Generate your first video to see activity here.</p>
                                <Link to="/video-generation" className="btn btn-primary">
                                    Generate video
                                </Link>
                            </div>
                        ) : (
                            <ul className="jobs-list">
                                {recentJobs?.jobs?.slice(0, 5).map((job) => (
                                    <li key={job.id} className="jobs-list__item">
                                        <div className="jobs-list__type">
                                            <div className="jobs-list__badge">{jobBadge(job.job_type)}</div>
                                            <div>
                                                <p className="jobs-list__title">{jobTypeLabel(job.job_type)}</p>
                                                <p className="jobs-list__meta">
                                                    {new Date(job.created_at).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`badge ${getStatusBadge(job.status)}`}>
                                            {job.status}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
